import React, { useEffect, useMemo } from "react";
import styles from "./ProgramGrade.module.scss";
import Button from "../button/Button";
import IconPencil from 'src/assets/icons/pencil.svg?react';
import IconArrowUp from 'src/assets/icons/dropdown.svg?react';
import FlagAU from 'src/assets/icons/icon_au.svg?react';
import FlagCA from 'src/assets/icons/icon_ca.svg?react';
import FlagUS from 'src/assets/icons/icon_us.svg?react';
import CoverMultiContent from '@src/components/CoverMultiContent/CoverMultiContent';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Collapse } from "react-collapse";
import { useHistory } from "react-router-dom";
import { SimpleMultiCollectionFragment, useGetMultiCollectionLazyQuery } from "@src/api/graphql-codegen";
import Spinner from "../spinner/Spinner";

const getFlagIcon = (country: String) => {
  switch (country) {
    case "Australia": {
      return <div style={{width: 40, height: 40}}><FlagAU width={40} /></div>;
    }
    case "Canada": {
      return <div style={{width: 40, height: 40}}><FlagCA width={40}/></div>;
    }
    case "USA": {
      return <div style={{width: 40, height: 40}}><FlagUS width={40}/></div>;
    }
    case "Canada & USA": {
      return (
        <>
          <div style={{width: 40, height: 40}}><FlagCA width={40}/></div>
          <div style={{width: 40, height: 40}}><FlagUS width={40}/></div>
        </>
      );
    }
    case "All Countries": {
      return <div><FlagAU width={40}/><FlagCA width={40}/><FlagUS width={40}/></div>;
    }
    default: {
      return null;
    }
  }
}

type Props = {
  programId?: string;
  multiCollection?: SimpleMultiCollectionFragment;
  selected?: boolean | false;
  selectedId?: string | null;
  handleSeleted?: (selectId: string) => void;
  handleDelete?: (mc: any) => void;
  handleEdit?: (mc: any) => void;
  handleAddMCCollection?: () => void;
};

function ProgramGrade(props: Props) {
  const { programId, multiCollection, selected, selectedId, handleSeleted, handleDelete, handleEdit, handleAddMCCollection } = props;

  const history = useHistory();

  const isSelected = useMemo(() => {
    return (selected && selectedId === multiCollection?.id) ? true : false;
  }, [selected, selectedId, multiCollection?.id]);

  const [getMultiCollection, {data, loading}] = useGetMultiCollectionLazyQuery({
    variables: {input: {id: `${multiCollection?.id}`}},
    fetchPolicy: 'network-only'});
    
  useEffect(() => {
    if (isSelected) {
      getMultiCollection();
    }
  }, [getMultiCollection, isSelected])

  const onEditCollection = (id: string) => {
    history.push(`/super/collections/edit/${id}`);
  }

  const handleCustomMultiCollection = () => {
    if (multiCollection?.id) {
      history.push(`/super/multi-collections/customize/${multiCollection.id}/?program=${programId}`);
    } else {
      handleAddMCCollection?.();
    }
  }

  const handleSelectGrade = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (multiCollection?.id) {
      handleSeleted?.(multiCollection.id);
    }
  }

  const handleDeleteGrade = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleDelete?.(multiCollection)
  }

  const handleEditGrade = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleEdit?.(multiCollection)
  }

  return (
    <div id={`${programId}-${multiCollection?.id}`} className={styles.card}>
      <div>
        <div className={styles.greyTitleView} style={{cursor: multiCollection ? 'pointer' : 'default'}} onClick={handleSelectGrade}>
          <div style={{display: "flex", alignItems: 'center', gap: 16}} >
            <IconArrowUp className={[styles.arrowIcon, isSelected ? styles.materialToggleClose : styles.materialToggleOpen].join(' ')} width={24} height={24} />
            <div style={{display: "flex", gap: '4px'}}>{getFlagIcon(`${multiCollection?.country}`)}</div>
            <h2>{multiCollection ? multiCollection.name : 'MULTI-Collections'}</h2>
            <span>{multiCollection?.id}</span>
          </div>
          {multiCollection && <div className={styles.action}>
            <Button outline={true} size='small' label='Delete M-C' color='red' className={styles.deleteButton} onClick={handleDeleteGrade}/>
            <Button outline={true} size='small' label='Edit M-C' className={styles.editButton} onClick={handleEditGrade}/>
            <div className={styles.viewCollection} rel="noreferrer" onClick={handleSelectGrade}>
              <span>{isSelected ? 'Hide Collections' : 'Show Collections'}</span>
              <IconArrowUp className={[styles.arrowDownIcon, isSelected ? styles.materialToggleClose : styles.materialToggleOpen].join(' ')} width={20} />
            </div>
          </div>}
        </div>
        <Collapse isOpened={!multiCollection ? true : isSelected}>
          <div className={styles.content}>
            <div className={styles.topUnitView}>
              {loading ? <div className={styles.loadingWrapper}><Spinner /></div> : <>
              {data?.getMultiCollection && data?.getMultiCollection.content && data?.getMultiCollection.content.length > 0 ? 
                (<div className={styles.topUnitSubView}>
                  <DndProvider backend={HTML5Backend}>
                    <div className={styles.lessonsView}>
                      <div className={styles.unitsDivWithFourGradient}>
                      {data?.getMultiCollection.content.map((collection: any, index: number) => {
                        return (
                          <CoverMultiContent key={index} content={collection} onEditCollection={onEditCollection}/>
                        )}
                      )}
                      </div>
                    </div>
                  </DndProvider>
                </div>
                ) : (
                  <div className={styles.emptyCollectionView}>
                    <h3>No Multi-Collections Available</h3>
                    <span>Please create or add a multi-collection</span>
                  </div>
                )
              }
              </>}
            </div>
            {<div>
              <div className={styles.hoverTitleView}>
                <div className={styles.hoverTitleSubView}>
                  <Button disabled={loading} size='small' outline={true} className={styles.editCollectionsButton} label={!data ? "Create or Add Multi-Collections" : "Edit Collections"}
                    leftIcon={loading ? <Spinner width={20} height={20} /> : <IconPencil className={styles.pencilIcon} />}
                    onClick={handleCustomMultiCollection}/>
                </div>
              </div>
            </div>}
          </div>
        </Collapse>
      </div>
    </div>
  );
}

export default ProgramGrade;
